import {
    Box, Button, FormControl, FormLabel, Input, NumberInput, NumberInputField,
    VStack, useColorModeValue, Stack, Center, Avatar, Text, Badge, Heading,
    IconButton, Alert, AlertIcon, Tooltip, ButtonGroup, FormErrorMessage, NumberInputStepper,
    NumberIncrementStepper, NumberDecrementStepper
  } from "@chakra-ui/react";
  import QrScanner from "react-qr-scanner";
  import { useSelector } from "react-redux";
  import { useState, useEffect, useRef, useCallback } from 'react';
  import { ToastService } from '../../services/toast/toastService';
  import { useAddStampToUserMutation } from "../../slices/adminApiSlice";
  import { useGetUserDataMutation } from "../../slices/userApiSlice";
  import Loader from "../../components/Loader";
  import { FaQrcode, FaTimes } from "react-icons/fa";
  
  const AdminAddStamps = () => {
    const { adminInfo } = useSelector(state => state.adminAuth);
    const [addStampToUser, { isLoading }] = useAddStampToUserMutation();
    const [getUserData] = useGetUserDataMutation();
    const { success, error } = ToastService();
    
    const [userData, setUserData] = useState(null);
    const [isQrScannerVisible, setQrScannerVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const inputRef = useRef(null);
    const typingTimeout = useRef(null);

    // State declarations
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const bgColor = useColorModeValue('white', 'gray.700');
    // const headerColor = useColorModeValue('gray.700', 'white');
    
    const [formData, setFormData] = useState({
      adminId: adminInfo?._id || "",
      userId: "",
      stamps: 0
    });
  
    useEffect(() => {
      setFormData(prev => ({ ...prev, adminId: adminInfo?._id || "" }));
    }, [adminInfo]);
  
    const updateUserData = useCallback(async (userId) => {
      try {
        const userResponse = await getUserData(userId).unwrap();
        setUserData(userResponse.user);
      } catch (err) {
        error(err.data?.message || 'An error occurred while fetching user data')
      }
    }, [getUserData, error]);    
  
    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prevData => ({ ...prevData, [name]: value }));
      
      clearTimeout(typingTimeout.current);
      
      // Only set timeout and call updateUserData if the changed field is 'userId'
      if (name === 'userId') {
          typingTimeout.current = setTimeout(() => updateUserData(value), 1000);
      }
    };
  
    const handleQrCodeButton = () => {
      setQrScannerVisible(prev => !prev);
      if (!isQrScannerVisible && inputRef.current) inputRef.current.focus();
    };
  
    // const handleScan = async (data) => {
    //   if (data) {
    //     setFormData(prev => ({ ...prev, userId: data.text }));
      
    //     const isUserValid = await updateUserData(data.text);
    //     if (!isUserValid) return; // Stop execution if user is not found
      
    //     setQrScannerVisible(false);
    //     success("QR Code Scanned Successfully!");
    //   }
    // };

    const handleScan = (data) => {
      if (data) {
          const userId = data.text;
          setFormData(prev => ({ ...prev, userId }));
          updateUserData(userId);
          setQrScannerVisible(false);
          success("QR Code Scanned Successfully!");
      }
    };
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.userId || formData.stamps <= 0) {
        setErrorMessage('Please fill in all required fields.');
        return;
      }
      try {
        setIsSubmitting(true);
        const response = await addStampToUser(formData).unwrap();
        success(response.message);
        setFormData({ adminId: adminInfo?._id || "", userId: "", stamps: 0 });
        updateUserData(formData.userId);
      } catch (err) {
        setErrorMessage(error.message || 'Failed to add stamps');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Stack 
        direction={{ base: 'column', md: 'row' }}
        spacing={8}
        align="stretch"
        width="100%"
      >
        {/* Left Panel - Form Section */}
        <Box 
          flex={{ base: 'none', md: 1 }}
          p={5}
          minWidth={{ base: '100%', md: '50%' }}
        >
          <Box 
            p={6}
            maxW={'450px'}
            w={'full'}
            borderRadius="xl"
            boxShadow={'xl'}
            bg={bgColor}
            borderWidth={'thin'}
            minH={isLoading ? "50vh" : "auto"}
            mx="auto"
          >
            {isLoading ? (
              <Center minH="50vh">
                <Loader color={bgColor}/>
              </Center>
            ) : (
              <form onSubmit={handleSubmit}>
                <VStack spacing={5} align="stretch">
                  {errorMessage && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      <Text>{errorMessage}</Text>
                    </Alert>
                  )}

                  <Tooltip label={isQrScannerVisible ? "Close scanner" : "Scan QR code"}>
                    <IconButton
                      icon={isQrScannerVisible ? <FaTimes /> : <FaQrcode />}
                      colorScheme="blue"
                      onClick={handleQrCodeButton}
                      aria-label="Toggle QR Scanner"
                      alignSelf="flex-end"
                    />
                  </Tooltip>
                  
                  {isQrScannerVisible && (
                    <Box 
                      mt={4}
                      borderWidth={1}
                      borderRadius="md"
                      p={4}
                      position="relative"
                    >
                      <QrScanner 
                        delay={300}
                        onError={() => setErrorMessage("Failed to scan QR code. Please try again.")}
                        onScan={handleScan}
                        style={{ width: '100%', borderRadius: '0.375rem' }}
                      />
                      <Text mt={2} fontSize="sm" color="gray.500">
                        Scan user's QR code
                      </Text>
                    </Box>
                  )}

                  <FormControl isRequired isInvalid={!formData.userId && formSubmitted}>
                    <FormLabel>User ID</FormLabel>
                    <Input 
                      name="userId"
                      placeholder="Enter User ID or scan QR"
                      value={formData.userId}
                      onChange={handleChange}
                      ref={inputRef}
                    />
                    {!formData.userId && formSubmitted && (
                      <FormErrorMessage>User ID is required</FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isRequired isInvalid={(formData.stamps <= 0 || formData.stamps > 10) && formSubmitted}>
                    <FormLabel>Stamps to Add</FormLabel>
                    <NumberInput 
                      min={1}
                      max={10}
                      value={formData.stamps}
                      onChange={(valStr, valNum) => {
                        if (valNum > 10) valNum = 10;
                        setFormData(prev => ({ ...prev, stamps: valNum }));
                      }}
                    >
                      <NumberInputField name="stamps" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>

                    {formSubmitted && formData.stamps <= 0 && (
                      <FormErrorMessage>Must add at least 1 stamp</FormErrorMessage>
                    )}
                    {formSubmitted && formData.stamps > 10 && (
                      <FormErrorMessage>Cannot add more than 10 stamps</FormErrorMessage>
                    )}
                  </FormControl>

                  <ButtonGroup spacing={4} mt={4}>
                    <Button 
                      type="submit"
                      colorScheme="teal"
                      flex={1}
                      isLoading={isSubmitting}
                      loadingText="Adding..."
                      isDisabled={
                        !formData.userId || 
                        formData.points <= 0
                      }
                    >
                      Add Stamp
                    </Button>
                    <Button 
                      colorScheme="gray"
                      onClick={() => {
                        setFormData({ adminId: adminInfo?._id || "", userId: "", stamps: 0 });
                        setFormSubmitted(false);
                        setErrorMessage('');
                      }}
                    >
                      Clear
                    </Button>
                  </ButtonGroup>
                </VStack>
              </form>
            )}
          </Box>
        </Box>
        
        {/* Right Panel - User Preview */}
        <Box 
          flex={{ base: 'none', md: 1 }}
          p={5}
          minWidth={{ base: '100%', md: '50%' }}
        >
          <Box 
            maxW={'320px'}
            p={6}
            w={'full'}
            bg={bgColor}
            boxShadow={'xl'}
            rounded={'lg'}
            textAlign={'center'}
            borderWidth={'thin'}
            mx="auto"
          >
            {userData ? (
              <>
                <Avatar 
                  size={'xl'}
                  src={userData?.image_url || '/default-user.png'}
                  mb={4}
                  name={userData?.name}
                />
                <Heading fontSize={'2xl'}>{userData.name}</Heading>
                <Text fontWeight={600} color={'gray.500'} mb={4}>
                  {userData.email || 'No email available'}
                </Text>
                
                <VStack spacing={3} mt={6}>
                  <Badge 
                    px={3}
                    py={1}
                    fontSize={'lg'}
                    colorScheme="green"
                    borderRadius="full"
                  >
                    {userData.current_stamps} Stamps
                  </Badge>
                  
                  <Badge 
                    px={3}
                    py={1}
                    fontSize={'md'}
                    colorScheme="blue"
                    borderRadius="full"
                  >
                    Member since {new Date(userData.createdAt).toLocaleDateString()}
                  </Badge>
                </VStack>
              </>
            ) : (
              <VStack spacing={4}>
                <Avatar size={'xl'} src="/default-user.png" mb={4} />
                <Heading fontSize={'xl'}>No user selected</Heading>
                <Text color={'gray.500'}>
                  {isQrScannerVisible 
                    ? "Scan a QR code or enter User ID" 
                    : "Enter a User ID to view details"}
                </Text>
              </VStack>
            )}
          </Box>
        </Box>
      </Stack>
      // <Stack direction={{ base: 'column', md: 'row' }} spacing={8}>
      //   {/* Left Panel - Form Section */}
      //   <Flex p={5} flex={1} align={'center'} justify={'center'}>
      //     <Box 
      //       p={6} 
      //       maxW={'450px'} 
      //       w={'full'} 
      //       borderRadius="xl" 
      //       boxShadow={'xl'} 
      //       bg={bgColor} 
      //       borderWidth={'thin'}
      //       minH={isLoading ? "50vh" : "auto"}
      //     >
      //       {isLoading ? (
      //         <Center minH="50vh">
      //           <Loader color={bgColor}/>
      //         </Center>
      //       ) : (
      //         <form onSubmit={handleSubmit}>
      //           <VStack spacing={5} align="stretch">
      //             {/* Error Message */}
      //             {errorMessage && (
      //               <Alert status="error" borderRadius="md">
      //                 <AlertIcon />
      //                 <Text>{errorMessage}</Text>
      //               </Alert>
      //             )}
  
      //             {/* QR Scanner Toggle */}
      //             <Tooltip label={isQrScannerVisible ? "Close scanner" : "Scan QR code"}>
      //               <IconButton
      //                 icon={isQrScannerVisible ? <FaTimes /> : <FaQrcode />}
      //                 colorScheme="blue"
      //                 onClick={handleQrCodeButton}
      //                 aria-label="Toggle QR Scanner"
      //                 alignSelf="flex-end"
      //               />
      //             </Tooltip>
                  
      //             {/* QR Scanner */}
      //             {isQrScannerVisible && (
      //               <Box 
      //                 mt={4} 
      //                 borderWidth={1} 
      //                 borderRadius="md" 
      //                 p={4}
      //                 position="relative"
      //               >
      //                 <QrScanner 
      //                   delay={300} 
      //                   onError={() => setErrorMessage("Failed to scan QR code. Please try again.")} 
      //                   onScan={handleScan}
      //                   style={{ width: '100%', borderRadius: '0.375rem' }}
      //                 />
      //                 <Text mt={2} fontSize="sm" color="gray.500">
      //                   Scan user's QR code
      //                 </Text>
      //               </Box>
      //             )}
  
      //             {/* User ID Input */}
      //             <FormControl isRequired isInvalid={!formData.userId && formSubmitted}>
      //               <FormLabel>User ID</FormLabel>
      //               <Input 
      //                 name="userId" 
      //                 placeholder="Enter User ID or scan QR" 
      //                 value={formData.userId} 
      //                 onChange={handleChange} 
      //                 ref={inputRef}
      //               />
      //               {!formData.userId && formSubmitted && (
      //                 <FormErrorMessage>User ID is required</FormErrorMessage>
      //               )}
      //             </FormControl>
  
      //             {/* Stamps Input */}
      //             <FormControl isRequired isInvalid={(formData.stamps <= 0 || formData.stamps > 10) && formSubmitted}>
      //             <FormLabel>Stamps to Add</FormLabel>
      //             <NumberInput 
      //               min={1} 
      //               max={10}  // Enforce max 10 stamps
      //               value={formData.stamps} 
      //               onChange={(valStr, valNum) => {
      //                 // Ensure the value does not exceed 10
      //                 if (valNum > 10) valNum = 10;
      //                 setFormData(prev => ({ ...prev, stamps: valNum }));
      //               }}
      //             >
      //               <NumberInputField name="stamps" />
      //               <NumberInputStepper>
      //                 <NumberIncrementStepper />
      //                 <NumberDecrementStepper />
      //               </NumberInputStepper>
      //             </NumberInput>

      //             {/* Error Message for Invalid Stamps */}
      //             {formSubmitted && formData.stamps <= 0 && (
      //               <FormErrorMessage>Must add at least 1 stamp</FormErrorMessage>
      //             )}
      //             {formSubmitted && formData.stamps > 10 && (
      //               <FormErrorMessage>Cannot add more than 10 stamps</FormErrorMessage>
      //             )}
      //           </FormControl>
  
      //             {/* Action Buttons */}
      //             <ButtonGroup spacing={4} mt={4}>
      //               <Button 
      //                 type="submit" 
      //                 colorScheme="teal" 
      //                 flex={1}
      //                 isLoading={isSubmitting}
      //                 loadingText="Adding..."
      //               >
      //                 Add Stamp
      //               </Button>
      //               <Button 
      //                 colorScheme="gray" 
      //                 onClick={() => {
      //                   setFormData({ adminId: adminInfo?._id || "", userId: "", stamps: 0 });
      //                   setFormSubmitted(false);
      //                   setErrorMessage('');
      //                 }}
      //               >
      //                 Clear
      //               </Button>
      //             </ButtonGroup>
      //           </VStack>
      //         </form>
      //       )}
      //     </Box>
      //   </Flex>
        
      //   {/* Right Panel - User Preview */}
      //   <Flex flex={1} align={'center'} justify={'center'}>
      //     <Box 
      //       maxW={'320px'} 
      //       p={6} 
      //       w={'full'} 
      //       bg={bgColor} 
      //       boxShadow={'xl'} 
      //       rounded={'lg'} 
      //       textAlign={'center'}
      //       borderWidth={'thin'}
      //     >
      //       {userData ? (
      //         <>
      //           <Avatar 
      //             size={'xl'} 
      //             src={userData?.image_url || '/default-user.png'} 
      //             mb={4}
      //             name={userData?.name}
      //           />
      //           <Heading fontSize={'2xl'}>{userData.name}</Heading>
      //           <Text fontWeight={600} color={'gray.500'} mb={4}>
      //             {userData.email || 'No email available'}
      //           </Text>
                
      //           <VStack spacing={3} mt={6}>
      //             <Badge 
      //               px={3} 
      //               py={1} 
      //               fontSize={'lg'} 
      //               colorScheme="green"
      //               borderRadius="full"
      //             >
      //               {userData.current_stamps} Stamps
      //             </Badge>
                  
      //             <Badge 
      //               px={3} 
      //               py={1} 
      //               fontSize={'md'} 
      //               colorScheme="blue"
      //               borderRadius="full"
      //             >
      //               Member since {new Date(userData.createdAt).toLocaleDateString()}
      //             </Badge>
      //           </VStack>
      //         </>
      //       ) : (
      //         <VStack spacing={4}>
      //           <Avatar size={'xl'} src="/default-user.png" mb={4} />
      //           <Heading fontSize={'xl'}>No user selected</Heading>
      //           <Text color={'gray.500'}>
      //             {isQrScannerVisible 
      //               ? "Scan a QR code or enter User ID" 
      //               : "Enter a User ID to view details"}
      //           </Text>
      //         </VStack>
      //       )}
      //     </Box>
      //   </Flex>
      // </Stack>
    );
  };
  
  export default AdminAddStamps;
  